import { api } from "./api";
import { registerPushServiceWorker } from "../serviceWorkerRegistration";

const DISMISS_KEY = "bcpl_push_prompt_dismissed";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    output[i] = raw.charCodeAt(i);
  }
  return output;
}

export function pushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

export async function fetchVapidPublicKey() {
  const { data } = await api.get("/push/vapid-public-key");
  if (!data?.configured || !data?.publicKey) return null;
  return data.publicKey;
}

export async function subscribeToPush() {
  if (!pushSupported()) {
    return { ok: false, reason: "unsupported" };
  }
  const publicKey = await fetchVapidPublicKey();
  if (!publicKey) {
    return { ok: false, reason: "not_configured" };
  }

  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    return { ok: false, reason: "denied" };
  }

  const registration = await registerPushServiceWorker();
  if (!registration) {
    return { ok: false, reason: "sw_failed" };
  }

  await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  const json = subscription.toJSON();
  await api.post("/push/subscribe", {
    endpoint: json.endpoint,
    keys: {
      p256dh: json.keys?.p256dh,
      auth: json.keys?.auth,
    },
    expirationTime: json.expirationTime ?? null,
  });
  return { ok: true, subscription };
}

export async function unsubscribeFromPush() {
  if (!pushSupported()) return;
  try {
    const registration = await navigator.serviceWorker.getRegistration("/");
    const subscription = await registration?.pushManager?.getSubscription();
    const endpoint = subscription?.endpoint;
    if (subscription) {
      await subscription.unsubscribe();
    }
    await api.post("/push/unsubscribe", { endpoint: endpoint || null });
  } catch {
    /* ignore — logout should still proceed */
  }
}

export function isPushPromptDismissed() {
  return localStorage.getItem(DISMISS_KEY) === "1";
}

export function dismissPushPrompt() {
  localStorage.setItem(DISMISS_KEY, "1");
}

/** Quiet auto-subscribe after login when permission already granted. */
export async function ensurePushSubscriptionQuiet() {
  if (!pushSupported()) return;
  if (Notification.permission !== "granted") return;
  try {
    await subscribeToPush();
  } catch {
    /* ignore */
  }
}
